/**
 * A new curated global.
 *
 * A kind and a name, in both languages — the two facts that decide what the record *is* and
 * what a parent will see it called. Everything else (description, identity notes, colour
 * anchor, axes) is on the detail page, for the same reason the Studio does it that way: a
 * create dialog that asks for eight fields is a form nobody finishes, and identity notes are
 * something you write once you can see the piece you are writing them for.
 *
 * The id is generated rather than typed. A global's id is a stable content reference — every
 * fork records `forkedFrom`, and the starters use slugs — so it is not a display name to be
 * edited later, and letting an admin invent one invites a collision with a fork's memory of it.
 */

import { Button, Dialog, Field, SelectField } from '@atlas-nook/design-system';
import type { PrimitiveKind } from '@atlas-nook/story';
import { useState } from 'react';
import type { JSX } from 'react';

import { createGlobal } from '../data/writes.js';
import { PRIMITIVE_KIND, entriesOf } from '../catalog/options.js';
import { ADMIN_STRINGS as S } from '../strings.js';

/** A slug from a name, uniquified against what is already curated. */
export function globalIdFor(name: string, taken: Set<string>): string {
  const base =
    name
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '') || 'piece';

  if (!taken.has(base)) return base;
  for (let n = 2; n < 100; n += 1) {
    if (!taken.has(`${base}-${n}`)) return `${base}-${n}`;
  }
  return `${base}-${Date.now()}`;
}

export function NewGlobalDialog({
  open,
  taken,
  onClose,
  onCreated,
}: {
  open: boolean;
  taken: Set<string>;
  onClose: () => void;
  onCreated: (id: string) => void;
}): JSX.Element {
  const [kind, setKind] = useState<PrimitiveKind>('character');
  const [nameEn, setNameEn] = useState('');
  const [nameTr, setNameTr] = useState('');
  const [error, setError] = useState<string | undefined>(undefined);
  const [working, setWorking] = useState(false);

  async function create(): Promise<void> {
    if (nameEn.trim() === '') {
      setError(S.globals.nameRequired);
      return;
    }
    setError(undefined);
    setWorking(true);

    const id = globalIdFor(nameEn, taken);
    try {
      await createGlobal({
        id,
        kind,
        name: {
          en: nameEn.trim(),
          ...(nameTr.trim() === '' ? {} : { tr: nameTr.trim() }),
        },
      });
      setNameEn('');
      setNameTr('');
      onCreated(id);
    } catch {
      setError(S.common.failed);
    } finally {
      setWorking(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={S.globals.newTitle}
      actions={
        <>
          <Button tone="quiet" onClick={onClose}>
            {S.common.cancel}
          </Button>
          <Button tone="primary" working={working} onClick={() => void create()}>
            {S.common.create}
          </Button>
        </>
      }
    >
      <p className="note">{S.globals.newBody}</p>
      <div className="stack">
        <SelectField
          label={S.globals.kind}
          value={kind}
          onChange={(value) => setKind(value as PrimitiveKind)}
        >
          {entriesOf(PRIMITIVE_KIND).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </SelectField>
        <Field
          label={S.globals.nameEn}
          value={nameEn}
          {...(error !== undefined ? { error } : {})}
          onChange={setNameEn}
        />
        <Field label={S.globals.nameTr} value={nameTr} onChange={setNameTr} />
      </div>
    </Dialog>
  );
}
